import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import apiFunctions from "../../services/api-service";
import { errorHandler } from "../../utils/HelperFunctions";
import useToast from "../../components/common/useToast";
import { ChatBubbleLeftRightIcon, MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline";
import { confirmDialog } from "primereact/confirmdialog";
import ChatMessageContent from "../../components/common/ChatMessageContent";

type ChatUser = { id: string; name: string; mobileno?: string; profileimg: string; online?: boolean };

type Conversation = {
    pair_key: string;
    messageCount: number;
    user1: ChatUser;
    user2: ChatUser;
    lastMessage: { message_type: string; preview: string; sender_id: string; createddate: string };
    // Set only when this row matched the global search via message content
    // (not just a participant's name) — jump straight to it on open.
    matchedMessageId?: string | null;
};

const OnlineDot: React.FC<{ online?: boolean; size?: number }> = ({ online, size = 12 }) => (
    <span
        style={{
            position: "absolute",
            bottom: -1,
            right: -1,
            width: size,
            height: size,
            borderRadius: "50%",
            background: online ? "#22c55e" : "#ef4444",
            border: "2px solid #fff",
        }}
    />
);

// Groups messages into date-separated sections, e.g. "MONDAY, JUN 15" — same
// visual grouping as the reference chat UI, minus any compose/send bar
// (admin here is a read-only observer, never a participant).
const dateLabel = (d: string) => {
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    if (sameDay(date, today)) return "TODAY";
    if (sameDay(date, yesterday)) return "YESTERDAY";
    return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }).toUpperCase();
};

const THREAD_TYPE_TABS: { value: string; label: string }[] = [
    { value: "", label: "All" },
    { value: "text", label: "Text" },
    { value: "media", label: "Media" },
    { value: "audio", label: "Audio" },
    { value: "files", label: "Files" },
];

const ChatPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast }: any = useToast();

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [totalConversations, setTotalConversations] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
    const [convPage, setConvPage] = useState(1);
    const [search, setSearch] = useState("");
    const CONV_LIMIT = 20;
    const conversationsListRef = useRef<HTMLDivElement>(null);

    // Top-level "search everything" bar — separate from the conversation
    // list search above and the in-thread search below.
    const [globalQuery, setGlobalQuery] = useState("");
    const [globalOpen, setGlobalOpen] = useState(false);
    const [globalUsers, setGlobalUsers] = useState<any[]>([]);
    const [globalMessages, setGlobalMessages] = useState<any[]>([]);
    const [globalLoading, setGlobalLoading] = useState(false);

    const [selected, setSelected] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<any[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const [threadPage, setThreadPage] = useState(1);
    const [threadTotalPages, setThreadTotalPages] = useState(1);

    const [threadSearchOpen, setThreadSearchOpen] = useState(false);
    const [threadSearchQuery, setThreadSearchQuery] = useState("");
    const [threadType, setThreadType] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchResultsLoading, setSearchResultsLoading] = useState(false);

    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Scroll bookkeeping — "bottom" on a fresh open (land on the latest
    // message like a real chat app), "preserve" on load-more-older (keep the
    // view pinned to what the admin was already reading, no jump).
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const scrollActionRef = useRef<"bottom" | "preserve" | null>(null);
    const preserveScrollRef = useRef<{ height: number; top: number } | null>(null);
    // While true, any image/video/map-embed that finishes loading inside the
    // thread re-pins the scroll to the bottom — those grow taller after the
    // initial scrollTop=scrollHeight, so without this the view lands short
    // of the actual latest message whenever it's IMAGE/VIDEO/LOCATION.
    const stickToBottomRef = useRef(false);

    const fetchConversations = async (searchTerm = "", page = 1, append = false) => {
        if (append) setLoadingMoreConversations(true);
        else setLoading(true);
        try {
            const res = await apiFunctions.get("/adminchat/conversations", {
                params: { page, limit: CONV_LIMIT, search: searchTerm },
            });
            if (res?.data?.success) {
                setConversations((prev) => (append ? [...prev, ...(res.data.data || [])] : (res.data.data || [])));
                setTotalConversations(res.data.total ?? (res.data.data || []).length);
                setConvPage(page);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setLoading(false);
            setLoadingMoreConversations(false);
        }
    };

    useEffect(() => {
        fetchConversations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const t = setTimeout(() => fetchConversations(search), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Infinite scroll for the conversation list — scrolling near the bottom
    // pulls in the next page until every conversation has been fetched.
    const loadMoreConversations = () => {
        if (!loading && !loadingMoreConversations && conversations.length < totalConversations) {
            fetchConversations(search, convPage + 1, true);
        }
    };

    const handleConversationsScroll = () => {
        const el = conversationsListRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 100) loadMoreConversations();
    };

    // Global search — users + messages across the whole platform, not just
    // the currently-open thread or the sidebar's name-only search.
    useEffect(() => {
        if (!globalQuery.trim()) { setGlobalUsers([]); setGlobalMessages([]); return; }
        setGlobalLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await apiFunctions.get("/adminchat/search", { params: { q: globalQuery.trim() } });
                if (res?.data?.success) {
                    setGlobalUsers(res.data.users || []);
                    setGlobalMessages(res.data.messages || []);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setGlobalLoading(false);
            }
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalQuery]);

    const goToUserProfile = (userId: string) => {
        setGlobalOpen(false);
        setGlobalQuery("");
        navigate(`/admin/profiles/view/${userId}`);
    };

    // Opens (or switches to) the conversation a global message-result belongs
    // to and jumps straight to that message — same in-context landing as the
    // in-thread search results.
    const goToGlobalMessage = (msg: any) => {
        setGlobalOpen(false);
        setGlobalQuery("");
        const conv: Conversation = {
            pair_key: [msg.sender.id, msg.receiver.id].sort().join("_"),
            messageCount: 0,
            user1: msg.sender,
            user2: msg.receiver,
            lastMessage: { message_type: msg.message_type, preview: msg.preview, sender_id: msg.sender.id, createddate: msg.createddate },
        };
        openConversation(conv, msg._id);
    };

    const globalSearchRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (globalSearchRef.current && !globalSearchRef.current.contains(e.target as Node)) {
                setGlobalOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    // Which conversation's thread is currently on screen — every async fetch
    // below is stamped with the pair_key it was issued for, and discards its
    // own result if the admin has since switched to a different conversation
    // (e.g. a "load more" fired for A, then the admin switches to B before it
    // resolves; without this guard, A's late result would splice into B's
    // messages and clobber B's fresh scroll-to-bottom with A's stale
    // preserved scroll position).
    const activeThreadKeyRef = useRef<string | null>(null);

    // Plain pagination of the real thread — never filtered by search/type,
    // those only drive the separate searchResults dropdown below.
    const fetchThreadMessages = async (c: Conversation, page: number, append: boolean) => {
        const requestKey = c.pair_key;
        if (append) {
            setLoadingMore(true);
            const el = messagesContainerRef.current;
            preserveScrollRef.current = el ? { height: el.scrollHeight, top: el.scrollTop } : null;
            scrollActionRef.current = "preserve";
        } else {
            setMessagesLoading(true);
            scrollActionRef.current = "bottom";
        }
        try {
            const res = await apiFunctions.get(`/adminchat/messages/${c.user1.id}/${c.user2.id}`, {
                params: { page, limit: 20 },
            });
            if (activeThreadKeyRef.current !== requestKey) return; // stale — a different conversation is open now
            if (res?.data?.success) {
                setMessages((prev) => (append ? [...prev, ...res.data.data] : res.data.data) || []);
                setThreadPage(res.data.page || page);
                setThreadTotalPages(res.data.totalPages || 1);
                // Page-1 fetches are unfiltered, so `total` here is the real
                // conversation size — always trust it over whatever the
                // sidebar list's row happened to carry, so the header never
                // shows a stale/placeholder count.
                if (!append && typeof res.data.total === "number") {
                    setSelected((prev) => (prev && prev.pair_key === requestKey ? { ...prev, messageCount: res.data.total } : prev));
                }
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            if (activeThreadKeyRef.current === requestKey) {
                setMessagesLoading(false);
                setLoadingMore(false);
            }
        }
    };

    const openConversation = (c: Conversation, jumpToId?: string) => {
        activeThreadKeyRef.current = c.pair_key;
        setSelected(c);
        setThreadSearchOpen(false);
        setThreadSearchQuery("");
        setThreadType("");
        setSearchResults([]);
        setMessages([]);
        if (jumpToId) jumpToMessage(c, jumpToId);
        else fetchThreadMessages(c, 1, false);
    };

    const loadMoreMessages = () => {
        if (selected && threadPage < threadTotalPages && !loadingMore && !messagesLoading) {
            fetchThreadMessages(selected, threadPage + 1, true);
        }
    };

    // Infinite scroll — scrolling near the top pulls in older messages,
    // repeatedly, until threadPage reaches threadTotalPages.
    const handleThreadScroll = () => {
        const el = messagesContainerRef.current;
        if (el && el.scrollTop < 80) loadMoreMessages();
    };

    // Applies whatever fetchThreadMessages queued up in scrollActionRef once
    // the new messages have actually rendered (container heights are final).
    useEffect(() => {
        const el = messagesContainerRef.current;
        // Skip the transient empty-array render from openConversation's
        // setMessages([]) — acting on it here would consume/reset the
        // "bottom" flag before the real messages ever arrive.
        if (!el || messages.length === 0) return;
        if (scrollActionRef.current === "bottom") {
            el.scrollTop = el.scrollHeight;
            stickToBottomRef.current = true;
            setTimeout(() => { stickToBottomRef.current = false; }, 1500);
        } else if (scrollActionRef.current === "preserve" && preserveScrollRef.current) {
            const grew = el.scrollHeight - preserveScrollRef.current.height;
            el.scrollTop = preserveScrollRef.current.top + grew;
        }
        scrollActionRef.current = null;
    }, [messages]);

    // Fires on every image `load`, iframe `load` (map embed), and video
    // `loadeddata` inside the thread (capture phase catches these non-
    // bubbling events even though they fire deep inside ChatMessageContent).
    const handleThreadMediaLoad = () => {
        const el = messagesContainerRef.current;
        if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
    };

    // WhatsApp-style: results appear in a dropdown as you type/pick a type
    // tab — the main thread stays untouched until a result is tapped.
    useEffect(() => {
        if (!selected || !threadSearchOpen) return;
        if (!threadSearchQuery.trim() && !threadType) { setSearchResults([]); return; }
        setSearchResultsLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await apiFunctions.get(`/adminchat/messages/${selected.user1.id}/${selected.user2.id}`, {
                    params: { page: 1, limit: 20, search: threadSearchQuery.trim(), type: threadType },
                });
                if (res?.data?.success) setSearchResults(res.data.data || []);
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setSearchResultsLoading(false);
            }
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadSearchQuery, threadType, threadSearchOpen]);

    // Tap a search result → land on it in context (a window of real thread
    // around it), then scroll to + flash-highlight the bubble.
    const jumpToMessage = async (c: Conversation, messageId: string) => {
        const requestKey = c.pair_key;
        setThreadSearchOpen(false);
        setSearchResults([]);
        setMessagesLoading(true);
        // Neither "bottom" nor "preserve" applies here — the highlight
        // effect below drives scrolling for a jump.
        scrollActionRef.current = null;
        try {
            const res = await apiFunctions.get(`/adminchat/messages/${c.user1.id}/${c.user2.id}`, {
                params: { aroundId: messageId },
            });
            if (activeThreadKeyRef.current !== requestKey) return; // stale — a different conversation is open now
            if (res?.data?.success) {
                setMessages(res.data.data || []);
                setThreadPage(1);
                setThreadTotalPages(1);
                setHighlightedMessageId(res.data.targetId || messageId);
                // A conversation opened straight from a global search hit is
                // a synthetic Conversation with a placeholder messageCount
                // (0) — the server knows the real total, so patch it in now.
                if (typeof res.data.totalMessages === "number") {
                    setSelected((prev) => (prev && prev.pair_key === requestKey ? { ...prev, messageCount: res.data.totalMessages } : prev));
                }
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            if (activeThreadKeyRef.current === requestKey) setMessagesLoading(false);
        }
    };

    const deleteMessage = (messageId: string) => {
        confirmDialog({
            header: "Delete Message",
            message: (
                <div className="flex items-center gap-3">
                    <TrashIcon className="w-5 h-5" style={{ color: "#ef4444" }} />
                    <span>Delete this message? This cannot be undone.</span>
                </div>
            ),
            accept: async () => {
                try {
                    const res = await apiFunctions.delete(`/adminchat/message/${messageId}`);
                    if (res?.data?.success) {
                        setMessages((prev) => prev.filter((m) => m._id !== messageId));
                        setSelected((prev) => (prev ? { ...prev, messageCount: Math.max(0, prev.messageCount - 1) } : prev));
                        showToast?.({ severity: "success", summary: "Deleted", detail: "Message deleted", life: 2000 });
                    }
                } catch (err) {
                    errorHandler(err as AxiosError);
                }
            },
        });
    };

    const deleteConversation = () => {
        if (!selected) return;
        confirmDialog({
            header: "Delete Conversation",
            message: (
                <div className="flex items-center gap-3">
                    <TrashIcon className="w-5 h-5" style={{ color: "#ef4444" }} />
                    <span>Delete the entire conversation between {selected.user1.name} and {selected.user2.name}? This cannot be undone.</span>
                </div>
            ),
            accept: async () => {
                try {
                    const res = await apiFunctions.delete(`/adminchat/conversation/${selected.user1.id}/${selected.user2.id}`);
                    if (res?.data?.success) {
                        showToast?.({ severity: "success", summary: "Deleted", detail: "Conversation deleted", life: 2000 });
                        activeThreadKeyRef.current = null;
                        setSelected(null);
                        setMessages([]);
                        fetchConversations(search);
                    }
                } catch (err) {
                    errorHandler(err as AxiosError);
                }
            },
        });
    };

    // Scroll the highlighted bubble into view once it's rendered, then clear
    // the highlight after a moment.
    useEffect(() => {
        if (!highlightedMessageId) return;
        const el = messageRefs.current[highlightedMessageId];
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        const t = setTimeout(() => setHighlightedMessageId(null), 2200);
        return () => clearTimeout(t);
    }, [highlightedMessageId, messages]);

    // Search results grouped by date, newest first — same date-header
    // treatment as the main thread.
    const groupedSearchResults = (() => {
        const groups: { label: string; items: any[] }[] = [];
        for (const m of searchResults) {
            const label = dateLabel(m.createddate);
            const last = groups[groups.length - 1];
            if (last && last.label === label) last.items.push(m);
            else groups.push({ label, items: [m] });
        }
        return groups;
    })();

    const resultPreview = (m: any) => {
        switch (m.message_type) {
            case "IMAGE": case "CAMERA": return "📷 Photo";
            case "VIDEO": return "🎥 Video";
            case "AUDIO": return "🎵 Audio";
            case "FILE": return `📄 ${m.file_name || "Document"}`;
            case "LOCATION": return "📍 Location";
            default: return m.message;
        }
    };

    // Group the (already newest-first) messages into date sections, oldest
    // section first, matching how a chat thread reads top-to-bottom.
    const groupedMessages = (() => {
        const chronological = [...messages].reverse();
        const groups: { label: string; items: any[] }[] = [];
        for (const m of chronological) {
            const label = dateLabel(m.createddate);
            const last = groups[groups.length - 1];
            if (last && last.label === label) {
                last.items.push(m);
            } else {
                groups.push({ label, items: [m] });
            }
        }
        return groups;
    })();

    return (
        <div className="dashboard-margin">
            {/* Global search — separate from the conversation-list search and
                the in-thread search: finds users AND messages across every
                conversation on the platform. Hidden once we've confirmed the
                platform has zero chats ever (not just a filtered-out list). */}
            {(loading || totalConversations > 0) && (
            <div ref={globalSearchRef} style={{ position: "relative", marginBottom: "12px" }}>
                <div className="flex items-center" style={{ background: "#fff", borderRadius: "8px", padding: "10px 14px", gap: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                    <MagnifyingGlassIcon className="w-5 h-5" style={{ color: "#9ca3af" }} />
                    <input
                        type="text"
                        placeholder="Search all users and messages..."
                        value={globalQuery}
                        onChange={(e) => { setGlobalQuery(e.target.value); setGlobalOpen(true); }}
                        onFocus={() => setGlobalOpen(true)}
                        style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontSize: "14px" }}
                    />
                    {globalQuery && (
                        <button
                            onClick={() => { setGlobalQuery(""); setGlobalOpen(false); }}
                            style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: "16px" }}
                        >
                            ×
                        </button>
                    )}
                </div>
                {globalOpen && globalQuery.trim() && (
                    <div
                        style={{
                            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
                            background: "#fff", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                            maxHeight: "420px", overflowY: "auto", border: "1px solid #e5e7eb",
                        }}
                    >
                        {globalLoading ? (
                            <div style={{ padding: "16px", fontSize: "13px", color: "#6b7280" }}>Searching...</div>
                        ) : globalUsers.length === 0 && globalMessages.length === 0 ? (
                            <div style={{ padding: "16px", fontSize: "13px", color: "#6b7280" }}>No users or messages match "{globalQuery}".</div>
                        ) : (
                            <>
                                {globalUsers.length > 0 && (
                                    <div>
                                        <div style={{ padding: "8px 14px", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.04em", background: "#f9fafb" }}>USERS</div>
                                        {globalUsers.map((u) => (
                                            <div
                                                key={u.id}
                                                onClick={() => goToUserProfile(u.id)}
                                                className="flex items-center gap-2 cursor-pointer"
                                                style={{ padding: "8px 14px", borderTop: "1px solid #f3f4f6" }}
                                            >
                                                <img src={u.profileimg} alt={u.name} style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover" }} />
                                                <div style={{ minWidth: 0 }}>
                                                    <p className="m-0" style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{u.name}</p>
                                                    <p className="m-0" style={{ fontSize: "11px", color: "#6b7280" }}>{u.mobileno}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {globalMessages.length > 0 && (
                                    <div>
                                        <div style={{ padding: "8px 14px", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.04em", background: "#f9fafb" }}>MESSAGES</div>
                                        {globalMessages.map((m) => (
                                            <div
                                                key={m._id}
                                                onClick={() => goToGlobalMessage(m)}
                                                className="flex items-center gap-2 cursor-pointer"
                                                style={{ padding: "8px 14px", borderTop: "1px solid #f3f4f6" }}
                                            >
                                                <img src={m.sender.profileimg} alt={m.sender.name} style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <p className="m-0" style={{ fontSize: "12px", fontWeight: 600, color: "#111827" }}>
                                                        {m.sender.name} → {m.receiver.name}
                                                    </p>
                                                    <p className="m-0" style={{ fontSize: "12px", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {m.preview}
                                                    </p>
                                                </div>
                                                <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", flexShrink: 0 }}>
                                                    {new Date(m.createddate).toLocaleDateString("en-IN")}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            )}
            <div className="flex" style={{ height: "calc(100vh - 190px)", background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" }}>
                {/* Conversation list */}
                <div className="flex flex-col" style={{ width: "320px", minWidth: "260px", borderRight: "1px solid #e5e7eb" }}>
                    <div style={{ padding: "16px" }}>
                        <div className="flex items-center gap-2" style={{ marginBottom: "12px" }}>
                            <ChatBubbleLeftRightIcon className="w-6 h-6" style={{ color: "#264164" }} />
                            <span style={{ fontWeight: 700, fontSize: "1.1rem", color: "#264164" }}>Messages</span>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#9ca3af" }}>({totalConversations})</span>
                        </div>
                        <div className="flex items-center" style={{ background: "#f3f4f6", borderRadius: "8px", padding: "8px 12px", gap: "8px" }}>
                            <MagnifyingGlassIcon className="w-4 h-4" style={{ color: "#9ca3af" }} />
                            <input
                                type="text"
                                placeholder="Search chats..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontSize: "13px" }}
                            />
                        </div>
                    </div>
                    <div ref={conversationsListRef} onScroll={handleConversationsScroll} style={{ flex: 1, overflowY: "auto" }}>
                        {loading ? (
                            <div style={{ padding: "16px", color: "#6b7280", fontSize: "13px" }}>Loading...</div>
                        ) : conversations.length === 0 ? (
                            <div className="flex flex-col items-center justify-center" style={{ padding: "32px 16px", color: "#6b7280", textAlign: "center" }}>
                                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                                    <ChatBubbleLeftRightIcon className="w-6 h-6" style={{ color: "#9ca3af" }} />
                                </div>
                                <span style={{ fontSize: "13px" }}>No conversations found.</span>
                            </div>
                        ) : (
                            conversations.map((c) => {
                                const isActive = selected?.pair_key === c.pair_key;
                                return (
                                    <div
                                        key={c.pair_key}
                                        onClick={() => openConversation(c, c.matchedMessageId || undefined)}
                                        className="flex items-center gap-2 cursor-pointer"
                                        style={{ padding: "10px 16px", borderBottom: "1px solid #f3f4f6", background: isActive ? "#eff6ff" : "transparent" }}
                                    >
                                        <div className="flex items-center" style={{ position: "relative", flexShrink: 0 }}>
                                            <span style={{ position: "relative", display: "inline-block" }}>
                                                <img src={c.user1.profileimg} alt={c.user1.name} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", background: "#f3f4f6" }} />
                                                <OnlineDot online={c.user1.online} />
                                            </span>
                                            <span style={{ position: "relative", display: "inline-block", marginLeft: "-12px", marginTop: "16px" }}>
                                                <img src={c.user2.profileimg} alt={c.user2.name} style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", background: "#f3f4f6", border: "2px solid #fff" }} />
                                                <OnlineDot online={c.user2.online} size={10} />
                                            </span>
                                        </div>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <p className="m-0" style={{ fontSize: "13px", fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {c.user1.name} &amp; {c.user2.name}
                                            </p>
                                            <p className="m-0" style={{ fontSize: "12px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {c.lastMessage?.preview}
                                            </p>
                                        </div>
                                        <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", flexShrink: 0 }}>
                                            {c.lastMessage?.createddate ? new Date(c.lastMessage.createddate).toLocaleDateString("en-IN") : ""}
                                        </p>
                                    </div>
                                );
                            })
                        )}
                        {loadingMoreConversations && (
                            <div className="flex justify-center" style={{ padding: "10px 0" }}>
                                <span style={{ fontSize: "11px", fontWeight: 600, color: "#9ca3af" }}>Loading more...</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Thread */}
                <div className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
                    {!selected ? (
                        <div className="flex flex-col items-center justify-center" style={{ flex: 1, color: "#6b7280", textAlign: "center", padding: "24px" }}>
                            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                                <ChatBubbleLeftRightIcon className="w-8 h-8" style={{ color: "#9ca3af" }} />
                            </div>
                            <p className="m-0" style={{ fontWeight: 700, fontSize: "1.1rem", color: "#111827" }}>Select a Conversation</p>
                            <p className="m-0" style={{ fontSize: "13px", marginTop: "6px" }}>Choose a chat from the left to view the messages.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between gap-4" style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb" }}>
                                <div className="flex items-center gap-4">
                                    {[selected.user1, selected.user2].map((u, i) => (
                                        <div key={u.id || i} className="flex items-center gap-2">
                                            <span style={{ position: "relative", display: "inline-block" }}>
                                                <img src={u.profileimg} alt={u.name} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                                                <OnlineDot online={u.online} size={10} />
                                            </span>
                                            <div>
                                                <p className="m-0" style={{ fontWeight: 600, fontSize: "13px", color: "#111827", lineHeight: 1.1 }}>{u.name}</p>
                                                <p className="m-0" style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", color: u.online ? "#16a34a" : "#9ca3af" }}>
                                                    {u.online ? "ONLINE" : "OFFLINE"}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center gap-3">
                                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#9ca3af", whiteSpace: "nowrap" }}>
                                        {selected.messageCount} message{selected.messageCount === 1 ? "" : "s"}
                                    </span>
                                    <button
                                    onClick={() => {
                                        const opening = !threadSearchOpen;
                                        setThreadSearchOpen(opening);
                                        if (!opening) { setThreadSearchQuery(""); setThreadType(""); }
                                    }}
                                    style={{
                                        width: 32, height: 32, borderRadius: "50%", border: "none", cursor: "pointer", flexShrink: 0,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        background: threadSearchOpen ? "#eff6ff" : "transparent",
                                        color: threadSearchOpen ? "#1d4ed8" : "#6b7280",
                                    }}
                                >
                                    <MagnifyingGlassIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={deleteConversation}
                                        title="Delete entire conversation"
                                        style={{
                                            width: 32, height: 32, borderRadius: "50%", border: "none", cursor: "pointer", flexShrink: 0,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            background: "transparent", color: "#ef4444",
                                        }}
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                            {threadSearchOpen && (
                                <div style={{ padding: "8px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div className="flex items-center" style={{ background: "#f3f4f6", borderRadius: "8px", padding: "6px 10px", gap: "8px" }}>
                                        <MagnifyingGlassIcon className="w-4 h-4" style={{ color: "#9ca3af" }} />
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Search within this chat..."
                                            value={threadSearchQuery}
                                            onChange={(e) => setThreadSearchQuery(e.target.value)}
                                            style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontSize: "13px" }}
                                        />
                                    </div>
                                    {/* WhatsApp-style type tabs — Text/Media/Audio/Files */}
                                    <div className="flex items-center gap-2">
                                        {THREAD_TYPE_TABS.map((tab) => (
                                            <button
                                                key={tab.value}
                                                onClick={() => setThreadType(tab.value)}
                                                style={{
                                                    border: "none",
                                                    borderRadius: "999px",
                                                    padding: "4px 12px",
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    cursor: "pointer",
                                                    background: threadType === tab.value ? "#1d4ed8" : "#f3f4f6",
                                                    color: threadType === tab.value ? "#fff" : "#374151",
                                                }}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Results — tap one to jump to it in the real thread below */}
                                    {(threadSearchQuery.trim() || threadType) && (
                                        <div style={{ maxHeight: "260px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
                                            {searchResultsLoading ? (
                                                <div style={{ padding: "12px", fontSize: "12px", color: "#6b7280" }}>Searching...</div>
                                            ) : searchResults.length === 0 ? (
                                                <div style={{ padding: "12px", fontSize: "12px", color: "#6b7280" }}>No matches found.</div>
                                            ) : (
                                                groupedSearchResults.map((group, gi) => (
                                                    <div key={gi}>
                                                        <div style={{ padding: "4px 10px", background: "#f9fafb", fontSize: "10px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.04em" }}>
                                                            {group.label.toUpperCase()}
                                                        </div>
                                                        {group.items.map((m: any) => {
                                                            const sender = m.sender_id === selected.user1.id ? selected.user1 : selected.user2;
                                                            return (
                                                                <div
                                                                    key={m._id}
                                                                    onClick={() => jumpToMessage(selected, m._id)}
                                                                    className="flex items-center gap-2 cursor-pointer"
                                                                    style={{ padding: "8px 10px", borderTop: "1px solid #f3f4f6" }}
                                                                >
                                                                    <img src={sender.profileimg} alt={sender.name} style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                        <p className="m-0" style={{ fontSize: "11px", fontWeight: 600, color: "#111827" }}>{sender.name}</p>
                                                                        <p className="m-0" style={{ fontSize: "12px", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                            {resultPreview(m)}
                                                                        </p>
                                                                    </div>
                                                                    <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", flexShrink: 0 }}>
                                                                        {new Date(m.createddate).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                                                    </p>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                ref={messagesContainerRef}
                                onScroll={handleThreadScroll}
                                onLoadCapture={handleThreadMediaLoad}
                                onLoadedDataCapture={handleThreadMediaLoad}
                                className="flex flex-col gap-3"
                                style={{ flex: 1, overflowY: "auto", padding: "16px" }}
                            >
                                {loadingMore && (
                                    <div className="flex justify-center" style={{ padding: "4px 0" }}>
                                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#9ca3af" }}>Loading older messages...</span>
                                    </div>
                                )}
                                {messagesLoading ? (
                                    <div style={{ textAlign: "center", color: "#6b7280" }}>Loading...</div>
                                ) : messages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center" style={{ padding: "24px 0", color: "#6b7280", textAlign: "center" }}>
                                        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "8px" }}>
                                            <ChatBubbleLeftRightIcon className="w-5 h-5" style={{ color: "#9ca3af" }} />
                                        </div>
                                        <span style={{ fontSize: "13px" }}>
                                            {threadSearchQuery || threadType ? "No messages match this filter." : "No messages in this conversation."}
                                        </span>
                                    </div>
                                ) : (
                                    groupedMessages.map((group, gi) => (
                                        <React.Fragment key={gi}>
                                            {group.label && (
                                                <div className="flex justify-center">
                                                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", background: "#f3f4f6", padding: "4px 12px", borderRadius: "999px" }}>
                                                        {group.label}
                                                    </span>
                                                </div>
                                            )}
                                            {group.items.map((m: any) => {
                                                const isFromUser1 = m.sender_id === selected.user1.id;
                                                const sender = isFromUser1 ? selected.user1 : selected.user2;
                                                const isHighlighted = m._id === highlightedMessageId;
                                                return (
                                                    <div
                                                        key={m._id}
                                                        className="flex items-end gap-2 group"
                                                        style={{ alignSelf: isFromUser1 ? "flex-start" : "flex-end", flexDirection: isFromUser1 ? "row" : "row-reverse", maxWidth: "70%" }}
                                                    >
                                                        <img src={sender.profileimg} alt={sender.name} title={sender.name} style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                        <div
                                                            ref={(el) => { messageRefs.current[m._id] = el; }}
                                                            style={{
                                                                position: "relative",
                                                                minWidth: 0,
                                                                background: isHighlighted ? "#fef08a" : (isFromUser1 ? "#f3f4f6" : "#dbeafe"),
                                                                borderRadius: "14px",
                                                                borderBottomLeftRadius: isFromUser1 ? "2px" : "14px",
                                                                borderBottomRightRadius: isFromUser1 ? "14px" : "2px",
                                                                padding: "10px 12px",
                                                                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                                                                transition: "background 0.4s ease",
                                                            }}
                                                        >
                                                            <button
                                                                onClick={() => deleteMessage(m._id)}
                                                                title="Delete message"
                                                                className="opacity-0 group-hover:opacity-100"
                                                                style={{
                                                                    position: "absolute", top: -8, [isFromUser1 ? "right" : "left"]: -8,
                                                                    width: 22, height: 22, borderRadius: "50%", border: "1px solid #e5e7eb",
                                                                    background: "#fff", color: "#ef4444", cursor: "pointer",
                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)", transition: "opacity 0.15s",
                                                                }}
                                                            >
                                                                <TrashIcon style={{ width: 12, height: 12 }} />
                                                            </button>
                                                            <p className="m-0" style={{ fontSize: "10px", fontWeight: 700, color: "#6b7280", marginBottom: "2px" }}>{sender.name}</p>
                                                            <ChatMessageContent message={m} searchQuery={threadSearchQuery.trim()} />
                                                            <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px", textAlign: "right" }}>
                                                                {new Date(m.createddate).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatPage;
